// frontend/src/pages/profile.jsx
// Updated: March 24, 2025
// Purpose: Agent profile management page with settings for name, states, and email preferences

import React, { useState } from 'react';
import apiService from '../services/apiService';
import { US_STATES, EMAIL_PREFERENCES } from '../constants';
import NavBar from '../components/NavBar';
import { useAuth } from '../auth/auth';

const ProfilePage = () => {
  const { user, setUser } = useAuth();
  const [formData, setFormData] = useState({
    name: user?.name || '',
    selectedStates: user?.selectedStates || [],
    emailPreference: user?.emailPreference || EMAIL_PREFERENCES.IMMEDIATE,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        selectedStates: checked
          ? [...prev.selectedStates, value]
          : prev.selectedStates.filter(state => state !== value),
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await apiService.updateAgentProfile({
        name: formData.name,
        selectedStates: formData.selectedStates,
        emailPreference: formData.emailPreference
      });
      
      if (response.data.success) {
        setUser({ ...user, ...formData });
        setSuccess('Profile updated successfully!');
      } else {
        setError(response.data.message || 'Failed to update profile.');
      }
    } catch (err) {
      console.error('Profile update error:', err);
      setError(err.response?.data?.message || 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <NavBar user={user} onLogout={() => window.location.href = '/'} isAdmin={false} />
      <div className="p-4 sm:p-6 max-w-2xl mx-auto">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4">Profile</h1>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 mb-4">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-800 rounded-md p-4 mb-4">
            {success}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
              required
            />
          </div>
          <div className="form-group">
            <label>States</label>
            <div className="border rounded-md p-2 max-h-40 overflow-y-auto grid grid-cols-2 gap-2">
              {US_STATES.map(state => (
                <div key={state.value} className="flex items-center">
                  <input
                    type="checkbox"
                    id={`state-${state.value}`}
                    name="selectedStates"
                    value={state.value}
                    checked={formData.selectedStates.includes(state.value)}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <label htmlFor={`state-${state.value}`}>{state.label}</label>
                </div>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="emailPreference">Email Preference</label>
            <select
              id="emailPreference"
              name="emailPreference"
              value={formData.emailPreference}
              onChange={handleChange}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
              required
            >
              <option value={EMAIL_PREFERENCES.IMMEDIATE}>Immediate</option>
              <option value={EMAIL_PREFERENCES.DAILY}>Daily Digest</option>
              <option value={EMAIL_PREFERENCES.WEEKLY}>Weekly Digest</option>
              <option value={EMAIL_PREFERENCES.DISABLED}>Disabled</option>
            </select>
          </div>
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update Profile'}
          </button>
        </form>
      </div>
    </>
  );
};

export default ProfilePage;